<template>
    <div class="file-upload display-flex flex-column w-100 align-items-center">
        <div class="file-upload-container flex-center flex-column h-100 w-100">
            <div
                class="file-drag-container flex-center flex-column w-100 p-md"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onFileDrop"
                :class="{ 'drag-active': isDragging }"
            >
                <h6>Drag your file here</h6>
                <p>Or</p>
                <div class="file-browser-container flex-center flex-column">
                    <input
                        id="file-input"
                        type="file"
                        ref="fileInput"
                        style="display: none"
                        @change="handleFileSelect"
                        :accept="allowedTypes.join(',')"
                    />
                    <AonButton
                        id="btn-open-dialog"
                        label="Browse"
                        width="60px"
                        :disabled="internalDisable"
                        @clicked="openFileDialog"
                    />
                    <div>({{ allowedTypes.join(', ') }})</div>
                </div>
            </div>
            <div v-if="showSelectedFile && selectedFile" class="selected-file">
                <font-awesome-icon icon="fas fa-file-arrow-up" class="edit-icon" size="lg" />
                <p>{{ selectedFile.name }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import mammoth from 'mammoth'
import { AonButton } from '@moatmetrics/armory/src/components/index.js'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import 'pdfjs-worker'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js'

const emit = defineEmits(['file-selected', 'file-parsed', 'file-parse-error'])

const fileInput = ref(null)
const selectedFile = ref(null)
const internalDisable = ref(false)
const isDragging = ref(false)

const props = defineProps({
    allowedTypes: {
        type: Array,
        default: () => ['.*'],
    },
    showSelectedFile: {
        type: Boolean,
        default: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
})

const openFileDialog = () => {
    fileInput.value.click()
}

const handleFileSelect = (event) => {
    internalDisable.value = true

    try {
        const file = event.target.files[0]
        if (file) {
            selectedFile.value = file

            emit('file-selected', file)

            parseFile(file)
        }
    } catch (error) {
        emit('file-parse-error', error)
    } finally {
        internalDisable.value = false
    }
}

const onDragOver = (event) => {
    isDragging.value = true
}

const onDragLeave = (event) => {
    isDragging.value = false
}

const onFileDrop = (event) => {
    isDragging.value = false
    internalDisable.value = true

    try {
        const file = event.dataTransfer.files[0]
        if (file) {
            // Check if the file type is allowed
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
            const isAllowed =
                props.allowedTypes.includes('.*') ||
                props.allowedTypes.some((type) => type === fileExtension || type === file.type)

            if (isAllowed) {
                selectedFile.value = file
                emit('file-selected', file)
                parseFile(file)
            } else {
                // Optional: Add error handling for invalid file types
                emit('file-parse-error', {
                    id: 'invalid-file-type',
                    message: 'Invalid file type',
                    fileExt: fileExtension,
                })
            }
        }
    } catch (error) {
        emit('file-parse-error', error)
    } finally {
        internalDisable.value = false
    }
}

const parseFile = async (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase()

    let parsedContent = null

    try {
        switch (fileExtension) {
            case 'txt':
                parsedContent = await readTextFile(file)
                break

            case 'docx':
                const fileBuffer = await file.arrayBuffer()
                const parseResult = await mammoth.extractRawText({ arrayBuffer: fileBuffer })

                if (parseResult.value) {
                    parsedContent = parseResult.value
                }

                break

            case 'pdf':
                const pdfBuffer = await file.arrayBuffer()

                const loadingTask = pdfjsLib.getDocument({ data: pdfBuffer })
                const pdfDocument = await loadingTask.promise

                let textContent = ''
                for (let i = 1; i <= pdfDocument.numPages; i++) {
                    const page = await pdfDocument.getPage(i)
                    const pageText = await page.getTextContent()
                    const pageContent = pageText.items.map((item) => item.str).join(' ')
                    textContent += pageContent + '\n'
                }

                parsedContent = textContent
                break
        }

        emit('file-parsed', parsedContent)
    } catch (error) {
        emit('file-parse-error', error)
    }
}

const readTextFile = (file) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        fileReader.onload = (e) => {
            resolve(e.target.result)
        }
        fileReader.onerror = (e) => {
            reject(e)
        }
        fileReader.readAsText(file)
    })
}
</script>

<style scoped lang="scss">
.file-drag-container {
    border: dashed var(--border-width-thick) var(--color-border-dark);
    gap: 5px;
    transition: all 0.2s ease;

    &.drag-active {
        border-color: var(--color-primary);
        background-color: var(--color-background-light);
    }
}

.file-browser-container {
    gap: 5px;
}
</style>
